import { PaymentModeEnum, useUpdateOrderMutation } from '@codezo/graphql';
import { OrderSettings, type OrderSettingsGroup } from '@codezo/order-detail';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { CurrencyInput } from '../../../components/ui/currency-input';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '../../../components/ui/select';
import { cn } from '../../../lib/utils';
import { getMutationError } from '../../../lib/mutation-response';

export interface OrderPaymentSettingsValues {
	productPaymentMode: PaymentModeEnum;
	samplePaymentMode: PaymentModeEnum;
	stripeCheckoutLimit?: number | null;
	invoiceMinimum?: number | null;
}

const PAYMENT_MODE_OPTIONS: { value: PaymentModeEnum; label: string }[] = [
	{ value: PaymentModeEnum.ClientChoice, label: 'Client choice' },
	{ value: PaymentModeEnum.InvoiceOnly, label: 'Invoice only' },
	{ value: PaymentModeEnum.StripeOnly, label: 'Stripe only' },
	{ value: PaymentModeEnum.AdminManual, label: 'Admin manual' },
];

interface FormState {
	productPaymentMode: PaymentModeEnum;
	samplePaymentMode: PaymentModeEnum;
	/** Kept as strings: an empty field means "not set", which is not zero. */
	stripeInput: string;
	invoiceInput: string;
}

function toFormState(settings: OrderPaymentSettingsValues): FormState {
	return {
		productPaymentMode: settings.productPaymentMode,
		samplePaymentMode: settings.samplePaymentMode,
		stripeInput:
			settings.stripeCheckoutLimit != null ? String(settings.stripeCheckoutLimit) : '',
		invoiceInput: settings.invoiceMinimum != null ? String(settings.invoiceMinimum) : '',
	};
}

function PaymentModeSelect({
	value,
	onChange,
}: {
	value: PaymentModeEnum;
	onChange: (v: PaymentModeEnum) => void;
}) {
	return (
		<Select
			items={PAYMENT_MODE_OPTIONS}
			value={value}
			onValueChange={(v) => {
				if (v) onChange(v as PaymentModeEnum);
			}}
		>
			<SelectTrigger className="w-56">
				<SelectValue />
			</SelectTrigger>
			<SelectContent>
				{PAYMENT_MODE_OPTIONS.map((opt) => (
					<SelectItem key={opt.value} value={opt.value}>
						{opt.label}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
}

/**
 * How this order's money is collected.
 *
 * Rendered through `OrderSettings` — the quote's own settings card — because the
 * quote holds these values first and the order copies them at acceptance. One
 * set of settings at two moments in its life, so it should not be two cards.
 *
 * What stays here is what only the app can own: the mutation, the form state,
 * and the controls themselves.
 *
 * ── Why it is still editable after acceptance ──
 *
 * These are a SNAPSHOT, not a mirror: editing the client's defaults afterwards
 * does not reach an order that has already been placed. So this card is the only
 * way to change how an order in flight gets paid for, which is why it is a form
 * rather than a read-only summary of what was copied.
 */
export function OrderPaymentSettings({
	orderId,
	settings,
	onSaved,
}: {
	orderId: string;
	settings: OrderPaymentSettingsValues;
	onSaved: () => void;
}) {
	const [updateOrder, { loading: saving }] = useUpdateOrderMutation();

	const baseline = useMemo(() => toFormState(settings), [settings]);
	const [form, setForm] = useState<FormState>(baseline);
	const [saved, setSaved] = useState(false);

	// Resync after a refetch, so a save elsewhere on the page doesn't leave this
	// form showing what the order used to say.
	useEffect(() => {
		setForm(baseline);
	}, [baseline]);

	const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
		setForm((prev) => ({ ...prev, [key]: value }));
		setSaved(false);
	};

	const dirty = useMemo(
		() => JSON.stringify(form) !== JSON.stringify(baseline),
		[form, baseline]
	);

	const handleSave = async () => {
		try {
			const { data } = await updateOrder({
				variables: {
					id: orderId,
					input: {
						productPaymentMode: form.productPaymentMode,
						samplePaymentMode: form.samplePaymentMode,
						// Empty clears the limit rather than setting it to zero — zero
						// would mean "nothing may go through Stripe", which is a
						// different instruction from "there is no ceiling".
						stripeCheckoutLimit:
							form.stripeInput === '' ? null : Number(form.stripeInput),
						invoiceMinimum: form.invoiceInput === '' ? null : Number(form.invoiceInput),
					},
				},
			});
			const error = getMutationError(data?.updateOrder);
			if (error) {
				toast.error(error);
				return;
			}
			toast.success('Payment settings updated');
			setSaved(true);
			onSaved();
		} catch {
			toast.error('Failed to update payment settings');
		}
	};

	const groups: OrderSettingsGroup[] = [
		{
			title: 'Payment modes',
			rows: [
				{
					key: 'productPaymentMode',
					label: 'Product payment mode',
					description: 'How product charges are collected',
					control: (
						<PaymentModeSelect
							value={form.productPaymentMode}
							onChange={(v) => set('productPaymentMode', v)}
						/>
					),
				},
				{
					key: 'samplePaymentMode',
					label: 'Sample payment mode',
					description: 'How sample charges are collected',
					control: (
						<PaymentModeSelect
							value={form.samplePaymentMode}
							onChange={(v) => set('samplePaymentMode', v)}
						/>
					),
				},
			],
		},
		{
			title: 'Limits',
			rows: [
				{
					key: 'stripeCheckoutLimit',
					label: 'Stripe checkout limit',
					description: 'Maximum payable through Stripe for this order',
					control: (
						<CurrencyInput
							currency="EUR"
							value={form.stripeInput}
							onChange={(e) => set('stripeInput', e.target.value)}
							placeholder="Not set"
							min={0}
							step={100}
							className="w-56"
						/>
					),
				},
				{
					key: 'invoiceMinimum',
					label: 'Invoice minimum',
					description: 'Minimum for invoice payment eligibility',
					control: (
						<CurrencyInput
							currency="DKK"
							value={form.invoiceInput}
							onChange={(e) => set('invoiceInput', e.target.value)}
							placeholder="Not set"
							min={0}
							step={1000}
							className="w-56"
						/>
					),
				},
			],
		},
	];

	return (
		<OrderSettings
			groups={groups}
			note="Snapshotted from the client's settings when this order was accepted — editing their defaults afterwards doesn't reach it."
			footer={
				<div className="flex flex-wrap items-center gap-3">
					<span
						className={cn(
							'inline-flex items-center gap-2 text-sm transition-colors',
							dirty
								? 'text-status-warn'
								: saved
									? 'text-status-success'
									: 'text-text-4'
						)}
					>
						<span
							className={cn(
								'size-1.5 rounded-full bg-current',
								dirty || saved ? 'opacity-100' : 'opacity-50'
							)}
						/>
						{dirty ? 'Unsaved changes' : saved ? 'All changes saved' : 'No changes'}
					</span>
					<div className="flex-1" />
					<Button
						variant="ghost"
						size="sm"
						onClick={() => {
							setForm(baseline);
							setSaved(false);
						}}
						disabled={!dirty || saving}
					>
						Discard
					</Button>
					<Button size="sm" onClick={handleSave} disabled={!dirty || saving}>
						{saving ? 'Saving…' : 'Save changes'}
					</Button>
				</div>
			}
		/>
	);
}
